import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  CheckBadgeIcon,
  DocumentTextIcon,
  PencilSquareIcon,
} from "@heroicons/react/24/outline";
import { PMDialog } from "../../utils/PMDialog";
import {
  DEFAULT_PROMANG_RICH_TEXT_CONTEXT_MENU,
  PromangRichTextEditor,
} from "../../utils/PromangRichTextEditor";
import { PromangRichTextContent } from "../../utils/PromangRichTextContent";
import { stripHtml } from "../../Utils";
import "../../utils/PromangRichTextEditor.css";

/** Quill empty / whitespace-only HTML → treat as blank. */
export function isBlankRichHtml(html) {
  return !stripHtml(html || "").trim();
}

/** Prefer `notes` / `manual_note` (API may land either name). */
export function resolveBasisNotes(basis) {
  if (!basis) return "";
  return basis.notes ?? basis.manual_note ?? "";
}

export function resolveEmployeeNote(row) {
  if (!row) return "";
  return row.manual_note ?? row.notes ?? "";
}

/**
 * Light sanitize for trusted-ish Quill HTML before display.
 * Strips scripts, iframes and inline event handlers (no DOMPurify dependency).
 */
export function sanitizePayrollNoteHtml(html) {
  const raw = html == null ? "" : String(html);
  if (!raw.trim()) return "";
  if (typeof document === "undefined") {
    return raw
      .replace(/<script[\s\S]*?>[\s\S]*?<\/script>/gi, "")
      .replace(/<iframe[\s\S]*?>[\s\S]*?<\/iframe>/gi, "")
      .replace(/\son\w+\s*=\s*("[^"]*"|'[^']*'|[^\s>]+)/gi, "");
  }
  const template = document.createElement("template");
  template.innerHTML = raw;
  template.content
    .querySelectorAll("script, iframe, object, embed, link[rel='import']")
    .forEach((el) => el.remove());
  template.content.querySelectorAll("*").forEach((el) => {
    [...el.attributes].forEach((attr) => {
      const name = attr.name.toLowerCase();
      if (name.startsWith("on") || name === "srcdoc") {
        el.removeAttribute(attr.name);
      }
      if (
        (name === "href" || name === "src" || name === "xlink:href") &&
        /^\s*javascript:/i.test(attr.value || "")
      ) {
        el.removeAttribute(attr.name);
      }
    });
  });
  return template.innerHTML;
}

function NotePreview({ html, emptyLabel, className = "" }) {
  if (isBlankRichHtml(html)) {
    return (
      <p className={`text-sm italic text-slate-400 ${className}`}>{emptyLabel}</p>
    );
  }
  return (
    <PromangRichTextContent
      html={sanitizePayrollNoteHtml(html)}
      className={`max-h-24 overflow-hidden text-sm text-slate-800 ${className}`}
    />
  );
}

/**
 * Generelle notater for hele lønnsgrunnlaget (periode).
 */
export function PayrollBasisGeneralNotes({
  value,
  editable,
  saving,
  onChange,
  onSave,
}) {
  const { t } = useTranslation();

  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-[0_1px_2px_rgba(0,0,0,.04),0_6px_22px_rgba(0,0,0,.07)]">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex min-w-0 items-start gap-3">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-[#EAF3FD] text-[#1463C7]">
            <DocumentTextIcon className="h-5 w-5" />
          </div>
          <div className="min-w-0">
            <h2 className="text-base font-semibold text-slate-900">
              {t("hours.payroll_basis.general_notes_title")}
            </h2>
            <p className="mt-0.5 text-sm text-slate-500">
              {t("hours.payroll_basis.general_notes_subtitle")}
            </p>
          </div>
        </div>
        {editable ? (
          <button
            type="button"
            onClick={onSave}
            disabled={saving}
            className="inline-flex min-h-[40px] items-center gap-2 rounded-lg px-4 py-2 text-sm font-semibold text-white shadow-[0_12px_24px_-8px_rgba(20,99,199,.55)] disabled:cursor-not-allowed disabled:bg-slate-200 disabled:text-slate-400 disabled:shadow-none"
            style={
              saving
                ? undefined
                : { background: "linear-gradient(135deg,#1463C7,#2D8BE8)" }
            }
          >
            {saving ? (
              <CheckBadgeIcon className="h-4 w-4 animate-pulse" />
            ) : null}
            {saving
              ? t("hours.payroll_basis.note_saving")
              : t("hours.payroll_basis.general_notes_save")}
          </button>
        ) : null}
      </div>

      <div className="mt-4">
        {editable ? (
          <PromangRichTextEditor
            value={value || ""}
            onChange={onChange}
            placeholder={t("hours.payroll_basis.general_notes_placeholder")}
            size="md"
            templatesEnabled={false}
            tablesEnabled={false}
            contextMenu={DEFAULT_PROMANG_RICH_TEXT_CONTEXT_MENU}
          />
        ) : isBlankRichHtml(value) ? (
          <p className="rounded-lg border border-dashed border-slate-200 bg-slate-50 px-3 py-4 text-sm italic text-slate-400">
            {t("hours.payroll_basis.general_notes_empty")}
          </p>
        ) : (
          <div className="rounded-lg border border-slate-100 bg-slate-50/80 px-3 py-3">
            <PromangRichTextContent html={sanitizePayrollNoteHtml(value)} />
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * Compact employee note cell + modal editor.
 */
export function PayrollBasisEmployeeNoteCell({
  row,
  value,
  editable,
  saving,
  onChange,
  onSave,
}) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState(value || "");

  useEffect(() => {
    if (!open) setDraft(value || "");
  }, [value, open]);

  const openEditor = () => {
    setDraft(value || "");
    setOpen(true);
  };

  const closeEditor = () => {
    setOpen(false);
    setDraft(value || "");
  };

  const handleSave = async () => {
    onChange?.(draft);
    await onSave?.(draft);
    setOpen(false);
  };

  const name =
    row?.user?.displayTitle || row?.user?.name || row?.user?.email || "";

  return (
    <>
      <div className="flex min-w-[200px] flex-col gap-2">
        <NotePreview
          html={value}
          emptyLabel={t("hours.payroll_basis.note_empty_preview")}
        />
        {editable ? (
          <button
            type="button"
            onClick={openEditor}
            className="inline-flex min-h-[36px] w-full items-center justify-center gap-1.5 rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-50"
          >
            <PencilSquareIcon className="h-3.5 w-3.5" />
            {isBlankRichHtml(value)
              ? t("hours.payroll_basis.note_add")
              : t("hours.payroll_basis.note_edit")}
            {saving ? (
              <CheckBadgeIcon className="h-3.5 w-3.5 animate-pulse text-[#1463C7]" />
            ) : null}
          </button>
        ) : null}
      </div>

      <PMDialog
        open={open}
        onClose={closeEditor}
        title="hours.payroll_basis.note_dialog_title"
        size="3xl"
        closeOnOutsideClick={false}
      >
        <div className="flex min-h-0 flex-1 flex-col gap-4">
          {name ? (
            <p className="text-sm text-slate-600">
              {t("hours.payroll_basis.note_dialog_employee", { name })}
            </p>
          ) : null}
          <div className="min-h-0 flex-1 overflow-y-auto">
            <PromangRichTextEditor
              value={draft}
              onChange={setDraft}
              placeholder={t("hours.payroll_basis.note_placeholder")}
              size="lg"
              templatesEnabled={false}
              tablesEnabled={false}
              contextMenu={DEFAULT_PROMANG_RICH_TEXT_CONTEXT_MENU}
            />
          </div>
          <div className="flex flex-wrap justify-end gap-2 border-t border-slate-100 pt-3">
            <button
              type="button"
              onClick={closeEditor}
              className="inline-flex min-h-[40px] items-center rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50"
            >
              {t("hours.payroll_basis.note_cancel")}
            </button>
            <button
              type="button"
              onClick={handleSave}
              disabled={saving}
              className="inline-flex min-h-[40px] items-center gap-2 rounded-lg px-4 py-2 text-sm font-semibold text-white shadow-[0_12px_24px_-8px_rgba(20,99,199,.55)] disabled:cursor-not-allowed disabled:bg-slate-200 disabled:text-slate-400 disabled:shadow-none"
              style={
                saving
                  ? undefined
                  : { background: "linear-gradient(135deg,#1463C7,#2D8BE8)" }
              }
            >
              {saving ? (
                <CheckBadgeIcon className="h-4 w-4 animate-pulse" />
              ) : null}
              {saving
                ? t("hours.payroll_basis.note_saving")
                : t("hours.payroll_basis.note_save")}
            </button>
          </div>
        </div>
      </PMDialog>
    </>
  );
}
